import React, { useState } from "react";

import { Button, debounce, Stack, styled, Switch, TextField, Typography } from "@mui/material";
import { RoleComponentGuard } from "components";
import { AccountLogo, AvatarUpload } from "components/forms";
import { FileViewer } from "components/popups";
import { useActionHook, useNavigate } from "hooks";
import { useAccountSelector, useUserActions, useUserSelector } from "store";
import { IFile, IUserActions } from "store/types";

import { FileType, RoleName } from "../../constants";

const Section = styled(Stack)({
  flexDirection: "row",
  padding: "30px 0",
  borderBottom: "1px solid #ced6d8"
});

const SectionTitle = styled(Typography)({
  width: "200px",
  fontSize: "0.8rem",
  fontWeight: 500
});

export const Profile: React.FC = () => {
  const account = useAccountSelector();
  const userNotiffication = useUserSelector().email_notification;
  const { first_name, email, last_name, phone_number, profile_photo_url, id } = useUserSelector();

  const [checked, setChecked] = useState<boolean>(!userNotiffication);
  const [pdf, setPdf] = useState<Pick<IFile, "file_url" | "type"> | null>(null);

  const {
    updateUserAction,
    updateAvatarAction,
    updateAccountLogoAction,
    getPrivacyPolicyAction,
    getTermsOfServiceAction
  } = useUserActions();

  const navigate = useNavigate();

  const { action: updateUser, error: userErrors } = useActionHook(
    async (data: Partial<Parameters<IUserActions["updateUserAction"]>[0]>) => {
      if (!id) return;
      await updateUserAction({ id, ...data });
    }
  );

  const { error: accountLogoErrors, action: updateAccountLogo } = useActionHook(async (logo: File) => {
    if (!account?.id) return;
    await updateAccountLogoAction({
      accountId: account.id,
      logo
    });
  });

  const updateAvatar = (avatar: File) => {
    if (!id) return;
    updateAvatarAction(id, avatar);
  };

  const updateUserDebounce = debounce(updateUser, 1000);

  const onClose = () => {
    setPdf(null);
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      updateUser({ email_notification: false });
      setChecked(true);
    } else {
      updateUser({ email_notification: true });
      setChecked(false);
    }
  };

  const showPrivacy = async () => {
    const { file_url } = await getPrivacyPolicyAction();
    if (!file_url) return;
    setPdf({
      file_url: file_url,
      type: FileType.PDF
    });
  };

  const showTerms = async () => {
    const { file_url } = await getTermsOfServiceAction();
    if (!file_url) return;
    setPdf({
      file_url: file_url,
      type: FileType.PDF
    });
  };

  return (
    <Stack
      sx={{
        p: "60px",
        bgcolor: "#f6f8fc",
        flexGrow: 1
      }}
    >
      <Typography
        sx={{
          fontSize: "2.5rem",
          fontFamily: "Oswald",
          color: "#071f35",
          mb: "50px"
        }}
      >
        Settings
      </Typography>
      <Stack sx={{ p: "20px 40px 60px 40px", flexGrow: 1, minWidth: 780, overflow: "auto" }}>
        <Section sx={{ alignItems: "flex-start" }}>
          <SectionTitle>Profile</SectionTitle>
          <Stack
            sx={{
              marginRight: "110px",
              flexGrow: 1,
              maxWidth: "460px",
              minWidth: "200px",
              fontFamily: "Lato",
              gap: "20px"
            }}
          >
            <TextField
              disabled
              label="Email Address"
              placeholder="jennysmith@capitalx.com"
              variant="outlined"
              size="small"
              fullWidth
              defaultValue={email}
              error={!!userErrors?.email}
              helperText={userErrors?.email}
              onChange={(event) => {
                updateUserDebounce({ email: event.target.value });
              }}
            />
            <TextField
              label="First Name"
              placeholder="Jenny"
              variant="outlined"
              size="small"
              fullWidth
              defaultValue={first_name}
              error={!!userErrors?.first_name}
              helperText={userErrors?.first_name}
              onChange={(event) => {
                updateUserDebounce({ first_name: event.target.value });
              }}
            />
            <TextField
              label="Last Name"
              placeholder="Smith"
              variant="outlined"
              size="small"
              fullWidth
              defaultValue={last_name}
              error={!!userErrors?.last_name}
              helperText={userErrors?.last_name}
              onChange={(event) => {
                updateUserDebounce({ last_name: event.target.value });
              }}
            />
            <TextField
              label="Phone Number"
              placeholder="Include your country code, e.g: +13022447595"
              variant="outlined"
              size="small"
              fullWidth
              defaultValue={phone_number}
              error={!!userErrors?.phone_number}
              helperText={userErrors?.phone_number}
              onChange={(event) => {
                updateUserDebounce({ phone_number: event.target.value });
              }}
            />
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
              hideChild={!account?.allow_social}
            >
              <Button onClick={() => navigate({ route: "socialProfile" })} fullWidth variant="outlined">
                Edit My Profile
              </Button>
            </RoleComponentGuard>
          </Stack>
          <AvatarUpload
            size={114}
            label="Upload"
            avatarUrl={profile_photo_url}
            onChange={({ file }: any) => updateAvatar(file)}
          />
        </Section>
        <Section>
          <SectionTitle>Password</SectionTitle>
          <Button
            onClick={() => navigate({ route: "changePassword" })}
            sx={{
              color: "#26a6ae",
              "&:hover": {
                backgroundColor: "transparent"
              }
            }}
          >
            Change Password
          </Button>
        </Section>
        <RoleComponentGuard availableRoles={[RoleName.Admin]}>
          <Section>
            <SectionTitle>Company Logo</SectionTitle>
            <AccountLogo
              src={account?.logo_url || ""}
              onChange={updateAccountLogo}
              errors={accountLogoErrors?.errors?.logo}
            />
          </Section>
        </RoleComponentGuard>
        <Section>
          <SectionTitle>Remove me from all email communication</SectionTitle>
          <Switch
            sx={{
              color: "#173c4c",
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#26a6ae"
              },
              "& .MuiSwitch-track": {
                color: "#f2f2f2"
              }
            }}
            defaultChecked={checked}
            onChange={handleCheck}
          />
        </Section>
        <Section sx={{ alignItems: "center" }}>
          <SectionTitle>Terms of service</SectionTitle>
          <Stack sx={{ flexDirection: "row", gap: "5px", alignItems: "center" }}>
            <Button
              onClick={showTerms}
              sx={{
                color: "#0a3142",
                textDecoration: "underline",
                padding: "1px",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline"
                }
              }}
            >
              Terms of service
            </Button>
            <Typography sx={{ color: "#0a3142" }}>&</Typography>
            <Button
              onClick={showPrivacy}
              sx={{
                color: "#0a3142",
                textDecoration: "underline",
                padding: "1px",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline"
                }
              }}
            >
              Privacy policy
            </Button>
          </Stack>
          {pdf?.file_url && <FileViewer file={pdf} isOpen={!!pdf} onClose={onClose} />}
        </Section>
      </Stack>
    </Stack>
  );
};
